import { isDisabled } from '@btcdirect/functions';
import { useApiCommunicator } from '@btcdirect/shared/src/composables/api/communicator';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { QuoteType } from '@btcdirect/shared/src/composables/api/quote/types';
import { InitializationStates } from '@btcdirect/shared/src/composables/api/types';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { CurrencyPair, useCurrenciesStore } from '@btcdirect/stores';
import { ref } from 'vue';

const state = ref<InitializationStates>(InitializationStates.NOT_INITIALIZED);

const { debug, warn } = useLogger();

export const useCurrencyPairs = () => {
    const currenciesStore = useCurrenciesStore();

    /**
     * Get the currency pairs.
     * If they have been retrieved before the saved values are returned. Otherwise, it will perform a call to the API.
     */
    const retrieveCurrencyPairs = async (quoteType?: QuoteType): Promise<void> => {
        if (state.value === InitializationStates.INITIALIZED && !!currenciesStore.currencyPairs.length) {
            return;
        }

        const { get } = useApiCommunicator(Endpoints.CURRENCY_PAIRS);

        state.value = InitializationStates.INITIALIZING;

        try {
            const pairs: CurrencyPair[] = await get();
            currenciesStore.setCurrencyPairs(pairs);

            debug('Successfully retrieved currency pairs');

            if (!quoteType || !currenciesStore.selectedCurrencyPair || !isDisabled(currenciesStore.selectedCurrencyPair, quoteType)) {
                return;
            }

            const firstEnabledCurrencyPair = currenciesStore.currencyPairs.find((currency) => !isDisabled(currency, quoteType));

            currenciesStore.setCurrencyPair(firstEnabledCurrencyPair);
        } catch (exception) {
            warn('An error occurred when retrieving currency pairs');
            currenciesStore.setCurrencyPairs(null);
        } finally {
            state.value = InitializationStates.INITIALIZED;
        }
    };

    return {
        retrieveCurrencyPairs
    };
};
